import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Countries, Flows } from 'src/app/app.consts';
import { UpdateLeadRequestModel } from 'src/app/models/lead.model';
import { ApplicationService } from 'src/app/services/application-service';
import { BasicInfoService } from 'src/app/services/basic-info.service';
import { FlowService } from 'src/app/services/flow.service';
import { TrackingService } from 'src/app/services/tracking.service';
import { LeadApiService } from 'src/app/services/api/lead-api.service';
import { environment } from 'src/environments/local/environment';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-high-amount-for-turnover',
  templateUrl: './high-amount-for-turnover.component.html',
  styleUrls: ['../account-data/account-data.component.scss', './high-amount-for-turnover.component.scss']
})
export class HighAmountForTurnoverComponent implements OnInit, OnDestroy {
  highAmountForm!: FormGroup;
  subs = Array<Subscription>();
  error: string = '';
  LIMIT_FI: number = 0.3; // 30% of monthly sales * 12
  LIMIT_NL: number = 0.2; // 20% of turnover
  LIMIT_SE: number = 0.2; // 20% of turnover
  THRESHOLD_FI: number = 10000; // Apps over €10k 
  THRESHOLD_NL: number = 10000; // Apps over €10k 
  THRESHOLD_SE: number = 100000; // Apps over 100k SEK
  MINIMUM_EURO: number = 1000;
  MINIMUM_SEK: number = 10000;
  tab!: null;
  market!: Countries;
  telemarketing!: boolean;
  
  constructor(
    private formBuilder: FormBuilder,
    private basicInfoService: BasicInfoService,
    private applicationService: ApplicationService,
    public flowService: FlowService,
    private translateService: TranslateService,
    private trackingService: TrackingService,
    private leadApi: LeadApiService,
  ) { }

  ngOnInit() {
    this.market = environment.country;
    this.telemarketing = this.flowService.flowName === Flows.TM_SIGN_UP;

    if (!this.isHighAmount) {
      this.flowService.nextScreen();
    } else {
      this.trackPageView('high-amount-for-turnover');
    }

    const initialAmount = this.basicInfoService.amount;

    this.highAmountForm = this.formBuilder.group({
      amount: [initialAmount],
    });
  }

  get isHighAmount(): boolean {
    if (this.basicInfoService.reason === 'Köp av fastighet' || this.basicInfoService.reason === 'Buying real estate') {
      return false;
    }
    if (this.market === Countries.FINLAND && 
        this.basicInfoService.amount > this.THRESHOLD_FI &&
        this.applicationService.estimatedMonthlySales > 0) {
        return this.basicInfoService.amount > (this.LIMIT_FI * 12 * this.applicationService.estimatedMonthlySales);
    }
    if (this.market === Countries.NETHERLANDS &&
        this.basicInfoService.amount > this.THRESHOLD_NL &&
        this.applicationService.estimatedTurnoverPrev > 0) {
        return this.basicInfoService.amount > (this.LIMIT_NL * this.applicationService.estimatedTurnoverPrev);
    }
    if (this.market === Countries.SWEDEN &&
        this.basicInfoService.amount > this.THRESHOLD_SE &&   
        this.applicationService.estimatedTurnoverPrev > 0) {
        return this.basicInfoService.amount > (this.LIMIT_SE * this.applicationService.estimatedTurnoverPrev);
    }
    return false;
  }

  trackFields(field: string): void {
    this.trackingService.filledField(field);
  }

  trackPageView(pageUrl: string): void {
    this.trackingService.trackPageView(pageUrl);
  }

  handleBackButtonClick(): void {
    this.flowService.previousScreen();
  }

  handleNextButtonClick(): void {
    if (!this.form.invalid) {
      if (this.basicInfoService.amount !== this.amount.value) {
        this.leadApi.updateLead(this.updateLead()).pipe(
          tap(() => {
            this.trackFields('changedAmount');
            // Emit difference in changed amount
          })
        ).subscribe(() => {
          this.basicInfoService.amount = this.amount.value;
        });
      }
      if (this.tab === 1) {
        this.trackFields('changeAmount');
      } else {
        this.trackFields('keepAmount');
      }
      this.flowService.nextScreen();
    }
  }

  updateLead(): UpdateLeadRequestModel {
    return {
      id: this.basicInfoService.leadId,
      email: this.basicInfoService.email,
      phoneNumber: this.basicInfoService.phoneNumber,
      amount: this.amount.value
    }
  }

  adjustToRecommendedAmount(): void {
    if (this.basicInfoService.amount === this.amount.value) {
      this.amount.setValue(this.recommendedAmount(this.market));
    } 
    this.amount.enable();
  }

  adjustToRequestedAmount(): void {
    this.amount.setValue(this.basicInfoService.amount);
    this.amount.disable();
  }

  recommendedAmount(country: Countries): number {
    if (country === Countries.FINLAND) {
      return Math.max(
        this.LIMIT_FI * 12 * this.applicationService.estimatedMonthlySales, 
        this.MINIMUM_EURO);
    }

    if (country === Countries.NETHERLANDS) {
      return Math.max(
        this.LIMIT_NL * this.applicationService.estimatedTurnoverPrev,
        this.MINIMUM_EURO);
    }

    return Math.max(
      this.LIMIT_SE * this.applicationService.estimatedTurnoverPrev, 
      this.MINIMUM_SEK);
  }

  get form(): FormGroup {
    return this.highAmountForm;
  }

  get amount(): FormControl {
    return this.highAmountForm.get('amount') as FormControl;
  }

  get currentAmount(): number {
    return this.basicInfoService.amount === this.amount.value
      ? this.basicInfoService.amount 
      : this.amount.value;
  }

  ngOnDestroy(): void {
    this.subs.map((s) => s.unsubscribe());
  }

  getErrors(control: string): string | null {
    const amount = this.amount;

    switch (control) {
      case 'amount':
        if (amount.hasError('invalidCurrencyMinimum')) {
          return this.translateService.instant('global.error.invalidCurrencyMinimum');
        }
        return null;
      default:
        return null;
    }
  }
}
